import { CAMINHO_OAUTH_GOOGLE, type ConexaoGoogle } from "@base/contracts";
import {
  Button,
  CabecalhoDePagina,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  EstadoCarregando,
  EstadoDeErro,
  cn,
  toast,
} from "@base/ui";
import { ArrowLeft, Check, LoaderCircle, LogIn, Search, Unplug } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { mensagemDoErro } from "../../../shared/http/mensagem-de-erro";
import { Link } from "../../../shared/navegacao/navegacao";
import { formatar } from "../../dashboard";
import {
  useConexaoGoogle,
  useContasGoogle,
  useCredencialGoogle,
  useDesconectarGoogle,
  useSalvarConexaoGoogle,
} from "../application/conexoes.contexto";
import { ResumoDoAcessoDaMonte, useRetornoDoOauthGoogle } from "./acesso-da-monte";

export const CAMINHO_DO_GOOGLE = CAMINHO_OAUTH_GOOGLE;

const normalizar = (texto: string) =>
  texto
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase();

function idVisivel(id: string): string {
  const digitos = id.replace(/\D/g, "");
  if (digitos.length === 10) return `${digitos.slice(0, 3)}-${digitos.slice(3, 6)}-${digitos.slice(6)}`;
  return id;
}

/** Configuracao do Google Ads: estado do login da Monte e contas desta clinica. */
export function PaginaDoGoogle({
  clienteId,
  cliente,
  equipeMonte,
}: {
  readonly clienteId: string;
  readonly cliente: string;
  readonly equipeMonte: boolean;
}) {
  // O login do Google (em Configuracoes da Monte) volta para esta rota.
  useRetornoDoOauthGoogle(equipeMonte);
  const credencial = useCredencialGoogle();
  const conexao = useConexaoGoogle(clienteId);
  const configurada = credencial.data?.configurada ?? false;

  return (
    <div className="flex flex-col gap-secao">
      <div className="flex flex-col gap-4">
        <Link
          para="/configuracoes/conexoes"
          className="inline-flex w-fit items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="size-4" aria-hidden="true" />
          Conexões
        </Link>
        <CabecalhoDePagina
          rotulo={`${cliente} · Conexões`}
          titulo="Google Ads"
          descricao="Campanhas de pesquisa, display e YouTube desta clínica."
        />
      </div>

      {credencial.isPending || conexao.isPending ? (
        <EstadoCarregando rotulo="Carregando o Google Ads" />
      ) : credencial.isError || conexao.isError ? (
        <EstadoDeErro mensagem={mensagemDoErro(credencial.error ?? conexao.error)} />
      ) : (
        <>
          <ResumoDoAcessoDaMonte
            plataforma="Google Ads"
            configurada={configurada}
            nome={credencial.data.nomeNoGoogle}
            equipeMonte={equipeMonte}
          />
          {configurada ? (
            <ContasDaClinica
              key={conexao.data.contas.join(",")}
              clienteId={clienteId}
              conexao={conexao.data}
            />
          ) : null}
        </>
      )}
    </div>
  );
}

function ContasDaClinica({ clienteId, conexao }: { readonly clienteId: string; readonly conexao: ConexaoGoogle }) {
  const contas = useContasGoogle(clienteId, true);
  const salvar = useSalvarConexaoGoogle(clienteId);
  const desconectar = useDesconectarGoogle(clienteId);
  const [escolhidas, setEscolhidas] = useState<ReadonlySet<string>>(() => new Set(conexao.contas));
  const [busca, setBusca] = useState("");
  const [confirmando, setConfirmando] = useState(false);
  const conectada = conexao.situacao !== "nao-conectada";

  const visiveis = useMemo(() => {
    const termo = normalizar(busca.trim());
    const digitos = termo.replace(/\D/g, "");
    return (contas.data ?? []).filter(
      (conta) => termo === "" || normalizar(conta.nome).includes(termo) || (digitos !== "" && conta.id.includes(digitos)),
    );
  }, [contas.data, busca]);

  const alterado =
    escolhidas.size !== conexao.contas.length || conexao.contas.some((conta) => !escolhidas.has(conta));

  function alternar(conjunto: ReadonlySet<string>, item: string): Set<string> {
    const proximo = new Set(conjunto);
    if (proximo.has(item)) proximo.delete(item);
    else proximo.add(item);
    return proximo;
  }

  return (
    <section className="flex flex-col gap-bloco">
      <div className="flex flex-col gap-1">
        <h2 className="text-xl font-normal tracking-tight">Contas desta clínica</h2>
        <p className="text-sm text-muted-foreground">Marque só as contas desta clínica.</p>
      </div>

      {conexao.ultimoErro ? (
        <p role="alert" className="rounded-2xl bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {conexao.ultimoErro}
        </p>
      ) : null}

      <div className="overflow-hidden rounded-3xl bg-card shadow-sm ring-1 ring-border/70">
        <label className="flex h-12 items-center gap-2 border-b px-5">
          <Search className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
          <input
            value={busca}
            onChange={(evento) => setBusca(evento.target.value)}
            placeholder="Buscar pelo nome ou ID"
            aria-label="Buscar conta de anúncio"
            className="w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
          />
          <span className="shrink-0 text-xs text-muted-foreground">{escolhidas.size} marcada(s)</span>
        </label>
        {contas.isPending ? (
          <p className="px-5 py-6 text-sm text-muted-foreground">Buscando as contas no Google Ads…</p>
        ) : contas.isError ? (
          <p role="alert" className="px-5 py-6 text-sm text-destructive">
            {mensagemDoErro(contas.error)}
          </p>
        ) : (
          <ul className="max-h-[26rem] overflow-y-auto p-2" aria-label="Contas de anúncio">
            {visiveis.map((conta) => {
              const marcada = escolhidas.has(conta.id);
              return (
                <li key={conta.id}>
                  <label
                    className={cn(
                      "flex cursor-pointer items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition-colors hover:bg-accent",
                      marcada && "bg-accent/60",
                    )}
                  >
                    <input
                      type="checkbox"
                      checked={marcada}
                      onChange={() => setEscolhidas((atual) => alternar(atual, conta.id))}
                      className="peer sr-only"
                    />
                    <span
                      aria-hidden="true"
                      className={cn(
                        "grid size-4 shrink-0 place-items-center rounded border peer-focus-visible:ring-2 peer-focus-visible:ring-primary/40",
                        marcada ? "border-primary bg-primary text-primary-foreground" : "border-input bg-background",
                      )}
                    >
                      {marcada ? <Check className="size-3" /> : null}
                    </span>
                    <span className="flex min-w-0 flex-1 flex-col">
                      <span className="truncate font-medium">{conta.nome}</span>
                      <span className="text-xs text-muted-foreground tabular-nums">{idVisivel(conta.id)}</span>
                    </span>
                    {!conta.ativa ? (
                      <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-[11px] text-muted-foreground">
                        Inativa
                      </span>
                    ) : null}
                  </label>
                </li>
              );
            })}
            {visiveis.length === 0 ? (
              <li className="px-3 py-6 text-center text-sm text-muted-foreground">Nenhuma conta encontrada.</li>
            ) : null}
          </ul>
        )}
      </div>

      {salvar.isError || desconectar.isError ? (
        <p role="alert" className="text-sm text-destructive">
          {mensagemDoErro(salvar.error ?? desconectar.error)}
        </p>
      ) : null}

      <div className="flex flex-wrap items-center justify-between gap-3">
        {conectada ? (
          <Button type="button" variant="outline" onClick={() => setConfirmando(true)}>
            <Unplug aria-hidden="true" />
            Desconectar desta clínica
          </Button>
        ) : (
          <span />
        )}
        <Button
          disabled={salvar.isPending || escolhidas.size === 0 || (conectada && !alterado)}
          onClick={() =>
            salvar.mutate({ contas: [...escolhidas] }, { onSuccess: () => toast.success("Contas salvas.") })
          }
        >
          {salvar.isPending ? <LoaderCircle className="animate-spin" aria-hidden="true" /> : null}
          {conectada ? "Salvar alterações" : "Conectar contas"}
        </Button>
      </div>

      <Dialog open={confirmando} onOpenChange={setConfirmando}>
        <DialogContent>
          <DialogHeader rotulo="Google Ads">
            <DialogTitle>Tirar o Google Ads desta clínica?</DialogTitle>
            <DialogDescription>O acesso da Monte continua. Só esta clínica deixa de usar as contas marcadas.</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
            <Button type="button" variant="outline" onClick={() => setConfirmando(false)}>
              Cancelar
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={desconectar.isPending}
              onClick={() =>
                desconectar.mutate(undefined, {
                  onSuccess: () => {
                    setConfirmando(false);
                    toast.success("Google Ads desconectado desta clínica.");
                  },
                })
              }
            >
              {desconectar.isPending ? <LoaderCircle className="animate-spin" aria-hidden="true" /> : null}
              Desconectar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}
